import { faPaperclip } from '@fortawesome/pro-light-svg-icons';
import { faArrowRightLong, IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, OutlinedInput, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { ChangeEvent, Fragment, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Mensagem } from '../../../interfaces/entities/SaudeBemEstar/ChatComEquipeGC/Mensagem';
import { MessageToSend } from '../../../interfaces/entities/SaudeBemEstar/ChatComEquipeGC/MessageToSend';
import { enviarMensagemPersonal, getAnexoMensagemPersonal, getMensagensPersonal } from '../../../services/PlanoMaisService';
import { getAnexoMensagem, getMensagens, novaMensagem } from '../../../services/SaudeBemEstar/GestaoDeCronicosService';
import { useAccessibility } from '../../../Store/contexts/AccessibilityContext';
import { downloadBase64File } from '../../../utils/DownloadFile';
import LoadingApp from '../../LoadingApp';
import ModalFeedBack from '../../ModalFeedBack';
import { VisuallyHiddenInput } from '../../VisuallyHiddenInput';
import ChatHeader from '../ChatHeader';
import { ChatTypeEnum } from '../ChatTypeEnum';
import Message from '../Message';
import './styles.css';
import { sxAttachFileButton, sxMessageInput } from './sxProps';

const Chat: React.FC<{ chatType: ChatTypeEnum }> = ({ chatType }) => {

  const { idMsgOrigem } = useParams();
  const [blankMessage, setBlankMessage] = useState<boolean>(false);
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [message, setMessage] = useState<string>('');
  const [loading, setLoading] = useState<{ show: boolean; message: string }>({ show: false, message: '' });
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [scrollBottom, setScrollBottom] = useState<boolean>(true);
  const [modalFeedback, setModalFeedback] = useState<null | {
    icon?: 'error' | 'success' | 'warning', title?: string, textButton?: string, subTitle?: string,
    closeAction: () => void, showButtonModal: boolean, buttonAction?: () => void
  }>(null);
  const [mensagemPai, setMensagemPai] = useState<Mensagem>();
  const isGC = chatType === ChatTypeEnum.GC;
  const isFinalized = mensagemPai?.status === 'CONCLUÍDA' || mensagemPai?.status === 'MF'; //STATUS CONCLUÍDA = GC; STATUS MF = PERSONAL

  const accessibility = useAccessibility();
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollBottom) scrollToBottom();
  }, [mensagens]);

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  };

  useEffect(() => {
    fetchMensagens(Number(idMsgOrigem), true);

    const millisecondsInterval = 10000;

    const intervalId = setInterval(() => {
      fetchMensagens(Number(idMsgOrigem), false);
    }, millisecondsInterval);

    return () => clearInterval(intervalId);
  }, []);

  const sendMessage = async (messageBody: MessageToSend) => {
    try {
      if (messageBody.anexoB64) {
        setLoading({ show: true, message: "Fazendo upload do arquivo..." })
      }
      const response = isGC ? await novaMensagem(messageBody) : await enviarMensagemPersonal(messageBody);
      if (response?.status === 200) {
        fetchMensagens(Number(idMsgOrigem), true);
      } else {
        console.error(response);
        openModal('error', 'Erro', 'Ocorreu um erro ao enviar sua mensagem. Tente novamente mais tarde.',
          'Fechar', () => window.history.back(), () => window.history.back());
      }
    } finally {
      setLoading({ show: false, message: '' });
    }
  }

  const fetchMensagens = async (idMsgOrigem: number, scroll: boolean) => {
    const response = isGC ? await getMensagens(idMsgOrigem) : await getMensagensPersonal(idMsgOrigem);
    if (response?.status === 200) {
      const mensagens = response.data;
      setScrollBottom(scroll);
      setMensagens(mensagens);
      setMensagemPai(mensagens[0]);
    } else {
      console.error(response);
      openModal('error', 'Erro', 'Ocorreu um erro ao buscar as mensagens. Tente novamente mais tarde.',
        'Fechar', () => window.history.back(), () => window.history.back());
    }
  }

  const openModal = (
    icon: 'success' | 'warning' | 'error', title: string, subTitle: string, textButton: string, closeAction: () => void, buttonAction: () => void
  ) => {
    setModalFeedback({
      icon,
      title,
      subTitle,
      textButton,
      showButtonModal: true,
      closeAction: closeAction,
      buttonAction: buttonAction
    });
  }

  const closeModalFeedback = () => {
    setModalFeedback(null);
  };

  const handleInputFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (file.size > 4 * 1000 * 1000) {
      openModal('warning', '', 'O arquivo selecionado é muito grande. Por favor, selecione um arquivo menor que 4MB.', 'Fechar', closeModalFeedback, closeModalFeedback);
      event.target.value = '';
      return;
    }

    if (!(/\.(pdf|jpg|png|doc|docx)$/i).test(file.name)) {
      openModal('warning', '', 'Somente são aceitos arquivos nos formatos .pdf, .jpg, .png, .doc ou .docx.', 'Fechar', closeModalFeedback, closeModalFeedback);
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      const [prefix, base64Data] = base64String.split(',');
      const anexo = {
        nomeArquivo: file.name,
        arquivo: base64Data,
        tipoArquivo: prefix
      };
      const messageBody =
        isGC ? { idMsgOrigem: idMsgOrigem || '', anexoB64: anexo.arquivo, nomeAnexo: anexo.nomeArquivo, mensagem: anexo.nomeArquivo }
          : { idMsgOrigem: idMsgOrigem || '', anexoB64: anexo.arquivo, nomeAnexo: anexo.nomeArquivo, mensagem: anexo.nomeArquivo, idDestinatario: mensagemPai?.idDestinatario || '' };
      sendMessage(messageBody);
      event.target.value = '';
    };
    reader.readAsDataURL(file);
  };

  const sendTextMessage = () => {
    if (message.trim() === '') {
      setBlankMessage(true);
      return;
    }
    const messageBody = isGC ? { idMsgOrigem: idMsgOrigem || '', mensagem: message } : { idMsgOrigem: idMsgOrigem || '', mensagem: message, idDestinatario: mensagemPai?.idDestinatario || '' };
    sendMessage(messageBody);
    setMessage('');
  };

  const downloadMessageAttachment = async (mensagem: Mensagem) => {
    setLoading({ show: true, message: "Baixando arquivo..."});
    const response = isGC ? await getAnexoMensagem(mensagem) : await getAnexoMensagemPersonal(mensagem);
    if (response?.status === 200) {
      downloadBase64File(response.data.anexo, response.data.descr);
    } else {
      console.error(response);
      openModal('error', 'Erro', 'Ocorreu um erro ao realizar o download. Tente novamente mais tarde.',
        'Fechar', () => window.history.back(), () => window.history.back());
    }
    setLoading({ show: false, message: '' });
  }

  const getDateText = (date: string) => {
    const today = dayjs().format('DD/MM/YYYY');
    const yesterday = dayjs().subtract(1, 'day').format('DD/MM/YYYY');
    if (date === today) {
      return 'hoje';
    } else if (date === yesterday) {
      return 'ontem';
    } else {
      return date;
    }
  };

  const handleInputMessage = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setMessage(event.target.value);
    if (value.trim() !== '') {
      setBlankMessage(false);
    }
  }

  return (
    <>
      <LoadingApp showLoading={loading.show} text={loading.message} />
      <div className='chat-container'>
        <ChatHeader chatTitle={mensagemPai?.mensagem || ''} chatSubtitle={`GC-${idMsgOrigem}`} chatType={chatType} />
        {isFinalized &&
          <div className='message-advice'>
            <Typography
              style={accessibility.textStyleAccessibility}
              fontSize={'14px'}
              fontFamily={'Unimed-Sans'}
            >
              Conversa concluída
            </Typography>
          </div>
        }
        <div className='messages-container' ref={messagesContainerRef}>
          {mensagens.map((msg, index, messages) => (
            <Fragment key={index}>
              {msg.dtMensagem !== messages[index - 1]?.dtMensagem &&
                <div className='date-separator'>
                  {getDateText(msg.dtMensagem)}
                </div>
              }
              {((isGC && msg.idMensagem !== idMsgOrigem) || (!isGC)) &&
                <Message key={index} mensagem={msg} downloadMessageAttachment={() => downloadMessageAttachment(msg)} isGC={isGC} />
              }
            </Fragment>
          ))
          }
        </div>
        {!isFinalized &&
          <div className='send-message-container'>
            <div className='text-message-container'>
              {blankMessage &&
                <div className='message-advice'>
                  <Typography
                    style={accessibility.textStyleAccessibility}
                    tabIndex={0}
                    fontSize={'14px'}
                    fontFamily={'Unimed-Sans'}
                  >
                    Digite uma mensagem para continuar
                  </Typography>
                </div>
              }
              <OutlinedInput
                placeholder='Digite sua mensagem'
                id='messageToSend'
                type={'text'}
                value={message}
                onChange={handleInputMessage}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    sendTextMessage();
                  }
                }}
                name='email'
                style={accessibility.textStyleAccessibility}
                endAdornment={
                  <IconButton onClick={sendTextMessage} aria-label="enviar mensagem">
                    <FontAwesomeIcon className='fa-icon24x24' icon={faArrowRightLong} color='#00995d'
                      style={accessibility.textStyleAccessibility}
                    />
                  </IconButton>
                }
                maxRows={1}
                inputProps={{ maxLength: 500 }}
                sx={sxMessageInput}
              />
            </div>
            <IconButton component="label"
              role={undefined} sx={sxAttachFileButton} tabIndex={-1} aria-label="anexar arquivo"
            >
              <FontAwesomeIcon className='fa-icon24x24' icon={faPaperclip as IconDefinition} color='#00995d'
                style={accessibility.textStyleAccessibility}
              />
              <VisuallyHiddenInput
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg"
                onChange={handleInputFile}
              />
            </IconButton>
          </div>
        }
      </div>
      <ModalFeedBack
        showModal={!!modalFeedback}
        handleShowModal={modalFeedback?.closeAction}
        title={modalFeedback?.title as string}
        subTitle={modalFeedback?.subTitle as string}
        icon={modalFeedback?.icon as 'success' | 'error' | 'warning'}
        showButtonModal={modalFeedback?.showButtonModal}
        textButton={modalFeedback?.textButton as string}
        handleButtonModal={modalFeedback?.buttonAction}
      />
    </>
  )
}

export default Chat;